import React from "react";
import {
  GridAggregationModel,
  GridColDef,
  GridColumnMenu,
  isAutogeneratedRow,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Box, ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import { VendorDTO } from "../../types/VendorDTO";
import VendorsToolbar from "./VendorsToolbar";
import StripedDataGrid from "../common/StripedDataGrid";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { withCaseInsensitiveStringFilters } from "../../utils/stringFilterOperators";
import { usePersistedGridState } from "../../hooks/usePersistedGridState";
import { applySavedColumnOrder } from "../../utils/gridStatePersistence";

type Props = {
  gridStorageKey: string;
  rows: VendorDTO[];
  loading: boolean;
  onRowClick: (id: string) => void;
  onAddVendor: () => void;
};

function getAggregationModelFromInitialState(
  initialState: any
): GridAggregationModel {
  const model = initialState?.aggregation?.model;

  if (!model || typeof model !== "object") {
    return {};
  }

  return model;
}

type ClearAggregationMenuItemProps = {
  colDef?: {
    field?: string;
  };
  hideMenu?: (event: React.MouseEvent) => void;
  aggregationModel?: GridAggregationModel;
  onAggregationModelChange?: (model: GridAggregationModel) => void;
};

function ClearAggregationMenuItem(props: ClearAggregationMenuItemProps) {
  const {
    colDef,
    hideMenu,
    aggregationModel = {},
    onAggregationModelChange,
  } = props;

  const field = colDef?.field;

  if (!field || !aggregationModel[field]) {
    return null;
  }

  return (
    <MenuItem
      onClick={(event) => {
        event.stopPropagation();

        const nextModel = { ...aggregationModel };
        delete nextModel[field];

        onAggregationModelChange?.(nextModel);
        hideMenu?.(event);
      }}
    >
      <ListItemIcon>
        <ClearIcon fontSize="small" />
      </ListItemIcon>

      <ListItemText>Usuń agregację</ListItemText>
    </MenuItem>
  );
}

function VendorsColumnMenu(props: any) {
  const {
    aggregationModel,
    onAggregationModelChange,
    ...columnMenuProps
  } = props;

  return (
    <GridColumnMenu
      {...columnMenuProps}
      slots={{
        columnMenuClearAggregationItem: ClearAggregationMenuItem as any,
      }}
      slotProps={{
        columnMenuClearAggregationItem: {
          displayOrder: 70,
          aggregationModel,
          onAggregationModelChange,
        } as any,
      }}
    />
  );
}

export default function VendorsGrid(props: Props) {
  const {
    gridStorageKey,
    rows,
    loading,
    onRowClick,
    onAddVendor,
  } = props;

  const { t } = useAppTranslation("common");
  const apiRef = useGridApiRef();

  const { initialState, scheduleSave } = usePersistedGridState(
    apiRef,
    gridStorageKey
  );

  const [aggregationModel, setAggregationModel] =
    React.useState<GridAggregationModel>(() =>
      getAggregationModelFromInitialState(initialState)
    );

  const aggregationSaveReadyRef = React.useRef(false);

  React.useEffect(() => {
    aggregationSaveReadyRef.current = false;
    setAggregationModel(getAggregationModelFromInitialState(initialState));
  }, [gridStorageKey, initialState]);

  React.useEffect(() => {
    if (!aggregationSaveReadyRef.current) {
      aggregationSaveReadyRef.current = true;
      return;
    }

    scheduleSave();
  }, [aggregationModel, scheduleSave]);

  const handleAggregationModelChange = React.useCallback(
    (model: GridAggregationModel) => {
      setAggregationModel(model);
    },
    []
  );

  const baseColumns = React.useMemo<GridColDef<VendorDTO>[]>(
    () => [
      withCaseInsensitiveStringFilters({
        field: "no",
        headerName: t("no"),
        flex: 0.6,
        sortable: true,
        filterable: true,
        aggregable: false,
      }) as GridColDef<VendorDTO>,

      withCaseInsensitiveStringFilters({
        field: "name",
        headerName: t("name"),
        flex: 1.6,
        sortable: true,
        filterable: true,
        aggregable: false,
      }) as GridColDef<VendorDTO>,

      withCaseInsensitiveStringFilters({
        field: "vatRegistrationNo",
        headerName: t("vatRegistrationNo"),
        flex: 1,
        sortable: true,
        filterable: true,
        aggregable: false,
      }) as GridColDef<VendorDTO>,

      withCaseInsensitiveStringFilters({
        field: "eMail",
        headerName: "Email",
        flex: 1.4,
        sortable: true,
        filterable: true,
        aggregable: false,
      }) as GridColDef<VendorDTO>,
    ],
    [t]
  );

  const columns = React.useMemo(
    () => applySavedColumnOrder(baseColumns, initialState),
    [baseColumns, initialState]
  );

  const isRealVendorRow = React.useCallback((params: any) => {
    if (!params?.row) return false;
    if (isAutogeneratedRow(params.row)) return false;
    if (!params.row.systemId) return false;

    return true;
  }, []);

  const handleRowClick = React.useCallback(
    (params: any) => {
      if (!isRealVendorRow(params)) {
        return;
      }

      onRowClick(String(params.row.systemId));
    },
    [isRealVendorRow, onRowClick]
  );

  const handleScheduleSave = React.useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  const mergedInitialState = React.useMemo(
    () => ({
      ...initialState,
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25,
        },
        ...(initialState as any)?.pagination,
      },
    }),
    [initialState]
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <StripedDataGrid
        key={gridStorageKey}
        apiRef={apiRef}
        initialState={mergedInitialState}
        sx={{
          flex: 1,
        }}
        rows={rows}
        getRowId={(r) => r.systemId}
        loading={loading}
        disableRowSelectionOnClick
        columns={columns}
        pagination
        paginationMode="client"
        pageSizeOptions={[10, 25, 50, 100]}
        aggregationModel={aggregationModel}
        onAggregationModelChange={handleAggregationModelChange}
        aggregationRowsScope="filtered"
        disableColumnPinning
        disablePivoting
        onPaginationModelChange={handleScheduleSave}
        onSortModelChange={handleScheduleSave}
        onFilterModelChange={handleScheduleSave}
        onColumnOrderChange={handleScheduleSave}
        onColumnWidthChange={handleScheduleSave}
        onColumnVisibilityModelChange={handleScheduleSave}
        onDensityChange={handleScheduleSave}
        onPreferencePanelClose={handleScheduleSave}
        onRowClick={handleRowClick}
        showToolbar
        slots={{
          toolbar: VendorsToolbar as any,
          columnMenu: VendorsColumnMenu as any,
        }}
        slotProps={{
          columnMenu: {
            aggregationModel,
            onAggregationModelChange: handleAggregationModelChange,
          } as any,
          toolbar: {
            title: t("Dostawcy"),
            total: rows.length,
            onAddVendor,
          } as any,
        }}
      />
    </Box>
  );
}